import { FC } from "react"
import DatePicker from "react-datepicker"
import { differenceInCalendarDays, endOfMonth, format, parseISO, startOfMonth, subDays, subMonths } from "date-fns"
import CalendarIcon from "@/shared/assets/images/CalendarBlank.svg"

export type AggregationInterval = 'day' | 'week' | 'month'

export type PeriodPreset = 'last_7_days' | 'last_30_days' | 'current_month' | 'previous_month' | 'custom'

export interface PeriodValue {
    preset: PeriodPreset
    from: string
    to: string
    interval: AggregationInterval
}

export interface PeriodPickerProps {
    value: PeriodValue
    onChange: (value: PeriodValue) => void
    withInterval?: boolean
    minDate?: Date
    maxDate?: Date
    disabled?: boolean
    className?: string
}

export const PERIOD_PRESETS: { value: PeriodPreset; label: string }[] = [
    { value: 'last_7_days', label: '7 дней' },
    { value: 'last_30_days', label: '30 дней' },
    { value: 'current_month', label: 'Текущий месяц' },
    { value: 'previous_month', label: 'Прошлый месяц' }
]

const INTERVAL_OPTIONS: { value: AggregationInterval; label: string }[] = [
    { value: 'day', label: 'День' },
    { value: 'week', label: 'Неделя' },
    { value: 'month', label: 'Месяц' }
]

const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd')

const normalizeDates = (from: Date, to: Date): { from: Date; to: Date } => {
    if (differenceInCalendarDays(from, to) <= 0) {
        return { from, to }
    }

    return { from: to, to: from }
}

const normalizePeriod = (period: PeriodValue): PeriodValue => {
    const dates = normalizeDates(parseISO(period.from), parseISO(period.to))

    return {
        ...period,
        from: formatDate(dates.from),
        to: formatDate(dates.to)
    }
}

const clampDate = (date: Date, minDate?: Date, maxDate?: Date): Date => {
    let nextDate = date

    if (minDate && differenceInCalendarDays(nextDate, minDate) < 0) {
        nextDate = minDate
    }

    if (maxDate && differenceInCalendarDays(nextDate, maxDate) > 0) {
        nextDate = maxDate
    }

    return nextDate
}

const normalizePeriodWithLimits = (period: PeriodValue, minDate?: Date, maxDate?: Date): PeriodValue => {
    const dates = normalizeDates(
        clampDate(parseISO(period.from), minDate, maxDate),
        clampDate(parseISO(period.to), minDate, maxDate)
    )

    return {
        ...period,
        from: formatDate(dates.from),
        to: formatDate(dates.to)
    }
}

export const buildPeriodFromPreset = (
    preset: Exclude<PeriodPreset, 'custom'>,
    interval: AggregationInterval = 'day',
    today: Date = new Date()
): PeriodValue => {
    switch (preset) {
    case 'last_7_days':
        return {
            preset,
            from: formatDate(subDays(today, 6)),
            to: formatDate(today),
            interval
        }
    case 'last_30_days':
        return {
            preset,
            from: formatDate(subDays(today, 29)),
            to: formatDate(today),
            interval
        }
    case 'current_month':
        return {
            preset,
            from: formatDate(startOfMonth(today)),
            to: formatDate(today),
            interval
        }
    case 'previous_month': {
        const previousMonth = subMonths(today, 1)

        return {
            preset,
            from: formatDate(startOfMonth(previousMonth)),
            to: formatDate(endOfMonth(previousMonth)),
            interval
        }
    }
    }
}

export const getDefaultPeriod = (interval: AggregationInterval = 'day'): PeriodValue => {
    return buildPeriodFromPreset('last_30_days', interval)
}

export const getPreviousPeriod = (value: PeriodValue): { from: string; to: string } => {
    const period = normalizePeriod(value)
    const from = parseISO(period.from)
    const to = parseISO(period.to)
    const daysCount = differenceInCalendarDays(to, from) + 1
    const previousTo = subDays(from, 1)
    const previousFrom = subDays(from, daysCount)

    return {
        from: formatDate(previousFrom),
        to: formatDate(previousTo)
    }
}

/**
 * Single shared period control for analytics screens: waves A2/A3 import it instead of building local range pickers.
 * The custom preset is selected implicitly when a caller edits dates, so it is not rendered as a clickable preset.
 */
export const PeriodPicker: FC<PeriodPickerProps> = ({
    value,
    onChange,
    withInterval = true,
    minDate,
    maxDate,
    disabled,
    className
}) => {
    const normalizedValue = normalizePeriodWithLimits(value, minDate, maxDate)

    const handlePresetChange = (preset: Exclude<PeriodPreset, 'custom'>) => {
        onChange(normalizePeriodWithLimits(buildPeriodFromPreset(preset, normalizedValue.interval), minDate, maxDate))
    }

    const handleDateChange = (field: 'from' | 'to', date: Date | null) => {
        if (!date) return

        onChange(normalizePeriodWithLimits({
            ...normalizedValue,
            preset: 'custom',
            [field]: formatDate(date)
        }, minDate, maxDate))
    }

    const handleIntervalChange = (interval: AggregationInterval) => {
        onChange({
            ...normalizedValue,
            interval
        })
    }

    return (
        <div className={`flex flex-col gap-3 ${className ?? ''}`}>
            <div className="flex flex-wrap items-center gap-2">
                {PERIOD_PRESETS.map((preset) =>
                    <button
                        key={preset.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => preset.value !== 'custom' && handlePresetChange(preset.value)}
                        className={`h-9 px-4 rounded-full text-[14px] font-semibold transition border border-color-dark disabled:opacity-50 disabled:cursor-not-allowed ${normalizedValue.preset === preset.value ? 'bg-color-dark text-white' : 'bg-white text-color-dark hover:bg-color-dark hover:text-white'}`}
                    >
                        {preset.label}
                    </button>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <div className="w-full sm:w-[180px]">
                    <DatePicker
                        selected={parseISO(normalizedValue.from)}
                        onChange={(date: Date | null) => handleDateChange('from', date)}
                        minDate={minDate}
                        maxDate={maxDate}
                        disabled={disabled}
                        wrapperClassName="react-datepicker-filterlike"
                        dateFormat={"dd.MM.yyyy"}
                        showIcon
                        enableTabLoop={false}
                        popperProps={{
                            placement: 'bottom'
                        }}
                        icon={(<CalendarIcon width={24} height={24} />)}
                    />
                </div>
                <div className="w-full sm:w-[180px]">
                    <DatePicker
                        selected={parseISO(normalizedValue.to)}
                        onChange={(date: Date | null) => handleDateChange('to', date)}
                        minDate={minDate}
                        maxDate={maxDate}
                        disabled={disabled}
                        wrapperClassName="react-datepicker-filterlike"
                        dateFormat={"dd.MM.yyyy"}
                        showIcon
                        enableTabLoop={false}
                        popperProps={{
                            placement: 'bottom'
                        }}
                        icon={(<CalendarIcon width={24} height={24} />)}
                    />
                </div>
                {withInterval &&
                    <div className="flex h-[52px] rounded-full bg-white overflow-hidden border border-color-dark">
                        {INTERVAL_OPTIONS.map((interval) =>
                            <button
                                key={interval.value}
                                type="button"
                                disabled={disabled}
                                onClick={() => handleIntervalChange(interval.value)}
                                className={`px-4 text-[14px] font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${normalizedValue.interval === interval.value ? 'bg-color-dark text-white' : 'text-color-dark hover:bg-color-dark hover:text-white'}`}
                            >
                                {interval.label}
                            </button>
                        )}
                    </div>
                }
            </div>
        </div>
    )
}
